import type { WindowDescriptor } from '@krp/client-runtime'

import type { OneCUiState } from '../../state'
import { supplyKey } from './model/keys'
import { decodeListFilter, decodeSection, listFilterChanged, sectionChosen } from './model/suppliesSlice'
import { EMPTY_LIST_FILTER } from './model/types'

/**
 * Окна поставок 1С (UB1c). Каждый адрес содержит `:rid`: identity окна —
 * база + поставка, поэтому поставка с тем же GUID в другой базе — другое окно,
 * и смена «выбранной базы» открытую поставку не перепривязывает (D-064).
 * Вкладка помнит фильтр списка и раздел поставки, а не ответы службы.
 */
export const ONEC_SUPPLY_WINDOWS = {
  list: 'onec.supplies',
  create: 'onec.supply-create',
  /** «Новая поставка» из файла накладной: после создания строки заполняются из этого файла (B-2 приёмки U9). */
  fromInvoice: 'onec.supply-from-invoice',
  supply: 'onec.supply',
  receipts: 'onec.supply-receipts',
} as const

export const suppliesWindows: readonly WindowDescriptor<OneCUiState>[] = [
  {
    kind: ONEC_SUPPLY_WINDOWS.list,
    scope: 'resource',
    paths: ['/resources/:rid/supplies'],
    label: 'Поставки 1С',
    capture: (state, params) => ({ ...(state.supplies.listFilter[(params.rid ?? '').toLowerCase()] ?? EMPTY_LIST_FILTER) }),
    restore: (snapshot, params) => {
      const filter = decodeListFilter(snapshot)
      return filter === undefined || params.rid === undefined ? [] : [listFilterChanged({ resourceId: params.rid, patch: filter })]
    },
  },
  {
    kind: ONEC_SUPPLY_WINDOWS.create,
    scope: 'resource',
    paths: ['/resources/:rid/supply-new'],
    label: 'Новая поставка 1С',
    size: 'md',
  },
  {
    // Файл — в адресе окна, а не в состоянии: окно переживает F5 и открывается ссылкой из окна файла.
    kind: ONEC_SUPPLY_WINDOWS.fromInvoice,
    scope: 'resource',
    paths: ['/resources/:rid/import/files/:fileId/supply-new'],
    label: 'Поставка из накладной',
    size: 'md',
  },
  {
    kind: ONEC_SUPPLY_WINDOWS.supply,
    scope: 'resource',
    paths: ['/resources/:rid/supplies/:supplyId'],
    label: 'Поставка 1С',
    size: 'full',
    capture: (state, params) => ({ section: state.supplies.section[supplyKey(params.rid ?? '', params.supplyId ?? '')] ?? 'lines' }),
    restore: (snapshot, params) => {
      const section = decodeSection(snapshot.section)
      return section === undefined || params.rid === undefined || params.supplyId === undefined
        ? []
        : [sectionChosen({ resourceId: params.rid, supplyId: params.supplyId, section })]
    },
  },
  {
    kind: ONEC_SUPPLY_WINDOWS.receipts,
    scope: 'resource',
    paths: ['/resources/:rid/supply-receipts'],
    label: 'Приходные в 1С',
    size: 'lg',
  },
]
